import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, EtatVide, IconeTelecharger, Message } from '../charte/Elements';
import { etatDocument, natureDocument } from '../ListeDocuments';
import { CHARTE, COULEURS } from '../../theme/charte';
import { DocumentClient, formatMontant } from '../../donnees/documents';
import { Mission } from '../../donnees/missions';
import { jourCourt } from '../../donnees/situation';
import { situationFinanciere } from '../../donnees/situationFinanciere';

/* Onglet Documents de la fiche, d'après la maquette MissionDocuments :
   chronologie contractuelle (devis, contrat, factures, avoirs, et la facture
   de solde à venir quand un acompte a été facturé), et à côté la situation
   de la mission (contrat, facturé, réglé, reste dû, reste à facturer). */

const nombre = (n: number) => Math.round(n).toLocaleString('fr-FR').replace(/ /g, ' ');
const pourcent = (part: number, total: number | null) => (total && total > 0 ? ` · ${Math.round((part / total) * 100)} %` : '');

const LigneSituation: React.FC<{ libelle: string; montant: string; fort?: boolean; couleur?: string }> = ({ libelle, montant, fort, couleur }) => (
  <div className="ligne ligne--entre" style={{ gap: '12px', flexWrap: 'nowrap', fontWeight: fort ? 600 : undefined }}>
    <span style={{ color: fort ? undefined : 'rgba(242,232,212,0.7)' }}>{libelle}</span>
    <span className="mono" style={{ color: couleur }}>{montant}</span>
  </div>
);

const DocumentsMission: React.FC<{
  mission: Mission;
  documents: DocumentClient[] | null;
  erreur: string;
  pdfEnCours: string | null;
  onPdf: (d: DocumentClient) => void;
}> = ({ mission, documents, erreur, pdfEnCours, onPdf }) => {
  if (erreur) return <Message genre="erreur">{erreur}</Message>;
  const docs = [...(documents ?? [])].sort((a, b) => (a.emisLe || '').localeCompare(b.emisLe || ''));
  const situation = situationFinanciere(mission, docs);
  const trace = mission.signatureDevis;

  const acompte = docs.find(d => d.type === 'FACTURE' && d.natureFacture === 'ACOMPTE' && d.etatPaiement !== 'ANNULEE');
  const solde = docs.some(d => d.type === 'FACTURE' && d.natureFacture === 'SOLDE' && d.etatPaiement !== 'ANNULEE');
  const soldeAVenir = !!acompte && !solde && mission.statut !== 'terminee'
    && situation.resteAFacturer != null && situation.resteAFacturer > 0;

  const detail = (d: DocumentClient) => {
    const feminin = d.type === 'FACTURE';
    const parts: string[] = [`${feminin ? 'Émise' : 'Émis'} le ${jourCourt(d.emisLe)}`];
    if (d.type === 'DEVIS' && trace?.devisNumero === d.numero) {
      parts.push(`signé le ${jourCourt(trace.signeLe)}${trace.signataire ? ` par ${trace.signataire}` : ''}`);
    }
    if (d.type === 'AVOIR' && d.documentOrigineNumero) parts.push(`sur ${d.documentOrigineNumero}`);
    parts.push(d.type === 'AVOIR' ? `− ${formatMontant(Math.abs(d.montantTtc))} TTC` : `${formatMontant(d.montantTtc)} TTC`);
    if (d.type === 'FACTURE' && d.resteDuTtc != null && d.resteDuTtc > 0) parts.push(`reste dû ${nombre(d.resteDuTtc)}`);
    return parts.join(' · ');
  };

  return (
    <div className="grille-fiche-documents">
      <section aria-labelledby="titre-chronologie" className="pile" style={{ gap: '14px' }}>
        <h2 id="titre-chronologie" style={{ fontSize: '20px' }}>Chronologie contractuelle</h2>
        {documents === null ? <EtatVide>Chargement des documents…</EtatVide> : docs.length === 0 ? (
          <div className="liste"><div className="liste__vide">Aucun document émis pour cette mission pour le moment.</div></div>
        ) : (
          <ol className="liste chronologie" aria-label="Documents de la mission">
            {docs.map(d => {
              const etat = etatDocument(d, mission, docs);
              const nature = natureDocument(d) + (d.type === 'FACTURE' && d.natureFacture !== 'TOTALITE' ? pourcent(d.montantTtc, situation.contrat) : '');
              const fond = etat.aRegler ? 'rgba(193,133,0,0.06)' : etat.aSigner ? 'rgba(212,74,15,0.05)' : undefined;
              const occupe = pdfEnCours === String(d.id);
              return (
                <li key={d.id} data-testid={`document-${d.id}`} className="chronologie__ligne" style={{ background: fond }}>
                  <span className="chronologie__point" aria-hidden style={{ background: etat.couleur }} />
                  <div className="liste__principal">
                    <div className="ligne" style={{ gap: '10px' }}>
                      <span className="mono" style={{ fontSize: '13px' }}>{d.numero}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600 }}>{nature}</span>
                      {(etat.aRegler || etat.aSigner) && (
                        <Badge libelle={etat.aRegler && d.etatPaiement === 'NON_PAYEE' && d.dateEcheance ? `À régler avant le ${jourCourt(d.dateEcheance)}` : etat.libelle}
                          couleur={etat.couleur} />
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: COULEURS.neutre }}>
                      {detail(d)}{!etat.aRegler && !etat.aSigner ? ` · ${etat.libelle.toLowerCase()}` : ''}
                    </div>
                  </div>
                  <div className="ligne" style={{ gap: '8px', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                    {etat.aSigner && <Link to={`/documents#devis-${d.id}`} className="bouton bouton--petit">Signer ce devis</Link>}
                    {etat.aRegler && <Link to="/documents#comment-regler" className="bouton bouton--secondaire bouton--petit">Comment régler</Link>}
                    <button type="button" className="bouton bouton--icone" onClick={() => onPdf(d)} disabled={occupe}
                      aria-label={`Télécharger le PDF ${d.numero}`} title="Télécharger le PDF">
                      {occupe ? <span className="roue" aria-hidden /> : <IconeTelecharger />}
                    </button>
                  </div>
                </li>
              );
            })}
            {soldeAVenir && (
              <li className="chronologie__ligne chronologie__ligne--a-venir" data-testid="solde-a-venir">
                <span className="chronologie__point chronologie__point--vide" aria-hidden />
                <div className="liste__principal">
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>Facture de solde{pourcent(situation.resteAFacturer ?? 0, situation.contrat)}</span>
                  <span style={{ fontSize: '13px', color: COULEURS.neutre }}>
                    À la livraison{mission.dateFin ? `, prévue le ${jourCourt(mission.dateFin)}` : ''} · {formatMontant(situation.resteAFacturer)} TTC
                  </span>
                </div>
                <div />
              </li>
            )}
          </ol>
        )}
      </section>

      <aside className="pile" style={{ gap: '16px' }}>
        <section className="carte carte--forte" style={{ padding: '22px 24px', gap: '14px' }} aria-labelledby="titre-situation">
          <span id="titre-situation" className="etiquette-mono" style={{ color: CHARTE.ocreClair }}>Situation de la mission</span>
          <div className="pile" style={{ gap: '10px', fontSize: '14px' }}>
            <LigneSituation libelle="Contrat" montant={situation.contrat != null ? nombre(situation.contrat) : '—'} />
            <LigneSituation libelle="Facturé" montant={nombre(situation.facture)} />
            <LigneSituation libelle="Réglé" montant={nombre(situation.regle)} />
            <div style={{ height: '1px', background: 'rgba(242,232,212,0.18)' }} />
            <LigneSituation libelle="Reste dû" montant={nombre(situation.resteDu)} fort couleur={CHARTE.orangeClair} />
            <LigneSituation libelle="Reste à facturer" montant={situation.resteAFacturer != null ? nombre(situation.resteAFacturer) : '—'} />
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(242,232,212,0.6)' }}>
            Montants TTC en F CFA (XOF).{situation.contrat == null ? ' Le montant du contrat apparaît après la signature du devis.' : ''}
          </div>
        </section>
        <div className="carte" style={{ padding: '20px 22px', gap: '8px' }}>
          <span className="etiquette-mono" style={{ color: CHARTE.ocreTexte }}>Comment régler</span>
          <p style={{ fontSize: '14px', lineHeight: 1.55 }}>
            Virement sur le RIB indiqué sur la facture, ou mobile money selon les indications qu'elle porte, avec le numéro de
            facture en référence. Rapprochement sous 2 jours ouvrés.
          </p>
        </div>
      </aside>
    </div>
  );
};

export default DocumentsMission;
